
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { useCookieConsent } from '@/contexts/CookieConsentContext'

interface AnalyticsPayload {
  page_path: string
  page_title?: string
  referrer?: string
  user_agent?: string
  screen_resolution?: string
  visit_duration?: number
  scroll_depth?: number
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
}

export const SiteAnalyticsTracker = () => {
  const location = useLocation()
  const { hasConsent } = useCookieConsent()
  const startTimeRef = useRef<number>(Date.now())
  const maxScrollRef = useRef<number>(0)
  const lastPathRef = useRef<string>('')

  // Track page view on route change
  useEffect(() => {
    // Check if Do Not Track is enabled
    if (navigator.doNotTrack === '1') {
      return
    }

    // Check for analytics consent
    if (!hasConsent('analytics')) {
      return
    }

    const trackPageView = async () => {
      // Track previous page if it exists
      if (lastPathRef.current) {
        await trackPageExit()
      }

      // Reset tracking for new page
      startTimeRef.current = Date.now()
      maxScrollRef.current = 0
      lastPathRef.current = location.pathname + location.search

      // Get URL parameters
      const urlParams = new URLSearchParams(location.search)
      
      // Skip tracking if this is a preview URL
      const fullPath = location.pathname + location.search
      if (fullPath.includes('__lovable_token=') || window.location.hostname.includes('lovableproject.com')) {
        return
      }
      
      const payload: AnalyticsPayload = {
        page_path: location.pathname + location.search,
        page_title: document.title,
        referrer: document.referrer,
        user_agent: navigator.userAgent,
        screen_resolution: `${screen.width}x${screen.height}`,
        utm_source: urlParams.get('utm_source') || undefined,
        utm_medium: urlParams.get('utm_medium') || undefined,
        utm_campaign: urlParams.get('utm_campaign') || undefined,
        utm_content: urlParams.get('utm_content') || undefined,
        utm_term: urlParams.get('utm_term') || undefined,
      }

      try {
        await supabase.functions.invoke('track-site-analytics', {
          body: payload
        })
      } catch (error) {
        console.warn('Analytics tracking failed:', error)
      }
    }

    trackPageView()
  }, [location, hasConsent])

  // Track scroll depth
  useEffect(() => {
    const trackScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight
      const scrollPercent = scrollHeight > 0 ? Math.round((scrollTop / scrollHeight) * 100) : 0
      
      if (scrollPercent > maxScrollRef.current) {
        maxScrollRef.current = scrollPercent
      }
    }

    window.addEventListener('scroll', trackScroll, { passive: true })
    return () => window.removeEventListener('scroll', trackScroll)
  }, [])

  // Track page exit (visit duration and scroll depth)
  const trackPageExit = async () => {
    if (!lastPathRef.current) return

    const visitDuration = Math.round((Date.now() - startTimeRef.current) / 1000)
    
    const payload: AnalyticsPayload = {
      page_path: lastPathRef.current,
      visit_duration: visitDuration,
      scroll_depth: maxScrollRef.current,
    }

    try {
      await supabase.functions.invoke('track-site-analytics', {
        body: payload
      })
    } catch (error) {
      console.warn('Exit tracking failed:', error)
    }
  }

  // Track page exit on beforeunload
  useEffect(() => {
    const handleBeforeUnload = () => {
      trackPageExit()
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  return null // This component doesn't render anything
}
